import { FC, useEffect, useMemo, useState } from 'react';
import { DateRange, RangeKeyDict } from 'react-date-range';
import moment from 'moment';
import { enUS } from 'date-fns/locale';
import cx from 'classnames';

// icons
import { ReactComponent as CloseIcon } from 'assets/icons/calendar/close.svg';

// styles
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import styles from './DateRangePicker.module.scss';

// types
import { Shortcuts } from './DateRangePicker.enum';

export interface DateRangeValue {
	startDate: Date | undefined;
	endDate: Date | undefined;
}

export enum InitialFocusedRange {
	StartDate = 0,
	EndDate = 1,
}

type DateRangePickerProps = {
	externalRange?: DateRangeValue;
	onChange?: (range: DateRangeValue) => void;
	onClose: () => void;
	initialFocusedRange?: InitialFocusedRange;
};

const today = new Date();

const DateRangePicker: FC<DateRangePickerProps> = ({
	externalRange,
	onChange,
	onClose,
	initialFocusedRange = InitialFocusedRange.StartDate,
}) => {
	const [range, setRange] = useState<DateRangeValue>({
		startDate: today,
		endDate: today,
	});

	const handleSelect = (ranges: RangeKeyDict) => {
		const selectedRange = ranges.selection;
		setRange({
			startDate: selectedRange.startDate,
			endDate: selectedRange.endDate,
		});
	};

	useEffect(() => externalRange && setRange(externalRange), [externalRange]);

	const handleShortcut = (type?: Shortcuts) => {
		switch (type) {
			case 'today':
				setRange({ startDate: today, endDate: today });
				break;
			case 'yesterday': {
				const yesterday = moment().subtract(1, 'day').toDate();
				setRange({ startDate: yesterday, endDate: yesterday });
				break;
			}
			case 'last7':
				setRange({ startDate: moment().subtract(6, 'days').toDate(), endDate: today });
				break;
			case 'last30':
				setRange({ startDate: moment().subtract(29, 'days').toDate(), endDate: today });
				break;
			case 'thisMonth':
				setRange({ startDate: moment().startOf('month').toDate(), endDate: today });
				break;
			case 'lastMonth':
				setRange({
					startDate: moment().subtract(1, 'month').startOf('month').toDate(),
					endDate: moment().subtract(1, 'month').endOf('month').toDate(),
				});
				break;
			default:
				setRange({ startDate: undefined, endDate: undefined });
				break;
		}
	};

	const isActiveShortcut = (type: Shortcuts): boolean => {
		const start = moment(range.startDate);
		const end = moment(range.endDate);
		const todayM = moment();

		switch (type) {
			case Shortcuts.Today:
				return range.startDate || range.endDate
					? start.isSame(todayM, 'day') && end.isSame(todayM, 'day')
					: false;

			case Shortcuts.Yesterday: {
				const y = todayM.clone().subtract(1, 'day');
				return start.isSame(y, 'day') && end.isSame(y, 'day');
			}

			case Shortcuts.Last7:
				return start.isSame(todayM.clone().subtract(6, 'day'), 'day') && end.isSame(todayM, 'day');

			case Shortcuts.Last30:
				return start.isSame(todayM.clone().subtract(29, 'day'), 'day') && end.isSame(todayM, 'day');

			case Shortcuts.ThisMonth:
				return (
					start.isSame(todayM.clone().startOf('month'), 'day') &&
					end.isSame(todayM, 'day') &&
					!!range.startDate &&
					!!range.endDate
				);

			case Shortcuts.LastMonth:
				return (
					start.isSame(moment().subtract(1, 'month').startOf('month'), 'day') &&
					end.isSame(moment().subtract(1, 'month').endOf('month'), 'day')
				);

			case Shortcuts.AllTime:
				return !range.startDate && !range.endDate;

			default:
				return false;
		}
	};

	const isCustomActive = (): boolean => {
		return !(
			isActiveShortcut(Shortcuts.Today) ||
			isActiveShortcut(Shortcuts.Yesterday) ||
			isActiveShortcut(Shortcuts.Last7) ||
			isActiveShortcut(Shortcuts.Last30) ||
			isActiveShortcut(Shortcuts.ThisMonth) ||
			isActiveShortcut(Shortcuts.LastMonth) ||
			isActiveShortcut(Shortcuts.AllTime)
		);
	};

	const onApply = () => {
		onChange?.({
			startDate: range.startDate,
			endDate: range.endDate,
		});

		onClose();
	};

	const selectionRange = useMemo(
		() => ({
			startDate: range.startDate,
			endDate: range.endDate,
			key: 'selection',
		}),
		[range]
	);

	return (
		<div className={styles.datePickerRange}>
			{/* header */}

			<div className={styles.datePickerRange__header}>
				<span className={styles.datePickerRange__header_title}>Date range</span>
				<CloseIcon className={styles.datePickerRange__header_close} onClick={onClose} />
			</div>

			{/* calendar */}
			<div className={styles.datePickerRange__calendar}>
				{/* shortcuts */}
				<div className={styles.datePickerRange__calendar__shortcuts}>
					<div className={styles.datePickerRange__calendar__shortcuts_custom}>
						<span
							className={cx(styles.datePickerRange__calendar__shortcuts_custom_item, {
								[styles.datePickerRange__calendar__shortcuts_custom_item_active]: isCustomActive(),
							})}
						>
							Custom
						</span>
					</div>
					<span
						className={cx(styles.datePickerRange__calendar__shortcuts_item, {
							[styles.datePickerRange__calendar__shortcuts_item_active]: isActiveShortcut(
								Shortcuts.Today
							),
						})}
						onClick={() => handleShortcut(Shortcuts.Today)}
					>
						Today
					</span>
					<span
						className={cx(styles.datePickerRange__calendar__shortcuts_item, {
							[styles.datePickerRange__calendar__shortcuts_item_active]: isActiveShortcut(
								Shortcuts.Yesterday
							),
						})}
						onClick={() => handleShortcut(Shortcuts.Yesterday)}
					>
						Yesterday
					</span>
					<span
						className={cx(styles.datePickerRange__calendar__shortcuts_item, {
							[styles.datePickerRange__calendar__shortcuts_item_active]: isActiveShortcut(
								Shortcuts.Last7
							),
						})}
						onClick={() => handleShortcut(Shortcuts.Last7)}
					>
						Last 7 days
					</span>
					<span
						className={cx(styles.datePickerRange__calendar__shortcuts_item, {
							[styles.datePickerRange__calendar__shortcuts_item_active]: isActiveShortcut(
								Shortcuts.Last30
							),
						})}
						onClick={() => handleShortcut(Shortcuts.Last30)}
					>
						Last 30 days
					</span>
					<span
						className={cx(styles.datePickerRange__calendar__shortcuts_item, {
							[styles.datePickerRange__calendar__shortcuts_item_active]: isActiveShortcut(
								Shortcuts.ThisMonth
							),
						})}
						onClick={() => handleShortcut(Shortcuts.ThisMonth)}
					>
						This month so far
					</span>
					<span
						className={cx(styles.datePickerRange__calendar__shortcuts_item, {
							[styles.datePickerRange__calendar__shortcuts_item_active]: isActiveShortcut(
								Shortcuts.LastMonth
							),
						})}
						onClick={() => handleShortcut(Shortcuts.LastMonth)}
					>
						Last month
					</span>
					<span
						className={cx(styles.datePickerRange__calendar__shortcuts_item, {
							[styles.datePickerRange__calendar__shortcuts_item_active]: isActiveShortcut(
								Shortcuts.AllTime
							),
						})}
						onClick={() => handleShortcut()}
					>
						All time
					</span>
				</div>

				{/* Calendar */}
				<div>
					<DateRange
						onChange={handleSelect}
						ranges={[selectionRange]}
						weekdayDisplayFormat="EEEEE"
						direction="vertical"
						scroll={{ enabled: true }}
						months={1.2}
						initialFocusedRange={[0, initialFocusedRange]}
						locale={enUS}
						dayContentRenderer={date => <span data-day={date.getDay()}>{date.getDate()}</span>}
						className={styles.datePickerRange__calendar__picker}
						startDatePlaceholder="From"
						endDatePlaceholder="To"
						classNames={{
							infiniteMonths: styles.datePickerRange__calendar__picker_infiniteMonths,

							// date input fields
							calendarWrapper: styles.datePickerRange__calendar__picker_calendarWrapper,
							dateDisplayWrapper: styles.datePickerRange__calendar__picker_dateDisplayWrapper,
							dateDisplay: styles.datePickerRange__calendar__picker_dateDisplay,
							dateDisplayItem: styles.datePickerRange__calendar__picker_dateDisplayItem,
							dateDisplayItemActive: styles.datePickerRange__calendar__picker_dateDisplayItemActive,

							// year and month pickers
							monthAndYearWrapper: styles.datePickerRange__calendar__picker_monthAndYearWrapper,
							monthAndYearPickers: styles.datePickerRange__calendar__picker_monthAndYearPickers,
							monthPicker: styles.datePickerRange__calendar__picker_monthPicker,
							yearPicker: styles.datePickerRange__calendar__picker_yearPicker,
							nextPrevButton: styles.datePickerRange__calendar__picker_nextPrevButton,
							prevButton: styles.datePickerRange__calendar__picker_prevButton,
							nextButton: styles.datePickerRange__calendar__picker_nextButton,

							// week days
							weekDays: styles.datePickerRange__calendar__picker_weekDays,
							weekDay: styles.datePickerRange__calendar__picker_weekDay,

							// calendar
							month: styles.datePickerRange__calendar__picker_month,
							monthName: styles.datePickerRange__calendar__picker_monthName,
							day: styles.datePickerRange__calendar__picker_day,
							dayNumber: styles.datePickerRange__calendar__picker_dayNumber,
							dayToday: styles.datePickerRange__calendar__picker_dayToday,
							inRange: styles.datePickerRange__calendar__picker_inRange,
							startEdge: styles.datePickerRange__calendar__picker_startEdge,
							endEdge: styles.datePickerRange__calendar__picker_endEdge,
							dayStartOfWeek: styles.datePickerRange__calendar__picker_dayStartOfWeek,
							dayEndOfWeek: styles.datePickerRange__calendar__picker_dayEndOfWeek,
							dayPassive: styles.datePickerRange__calendar__picker_dayPassive,
							dayStartPreview: styles.datePickerRange__calendar__picker_dayStartPreview,
							dayEndPreview: styles.datePickerRange__calendar__picker_dayEndPreview,
							dayInPreview: styles.datePickerRange__calendar__picker_dayInPreview,
							dayStartOfMonth: styles.datePickerRange__calendar__picker_dayStartOfMonth,
							dayEndOfMonth: styles.datePickerRange__calendar__picker_dayEndOfMonth,
							dayHovered: styles.datePickerRange__calendar__picker_dayHovered,
						}}
					/>
				</div>
			</div>

			{/* footer */}

			<div className={styles.datePickerRange__footer}>
				<span className={styles.datePickerRange__footer_button} onClick={onApply}>
					Apply
				</span>
			</div>
		</div>
	);
};

export default DateRangePicker;
